import React from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { AutoFormFieldProps } from "@autoform/react";
import _ from "lodash";
import { Plus, Trash } from "lucide-react";

import { BadgeSelector } from "@/components/fields/badge-selector";
import { RemoteSelect } from "@/components/fields/remote-select";
import { PhotoWithBlurhash } from "@/components/media/photo-with-blurhash";
import { SortableList } from "@/components/ui/auto-form/fields/sortable";
import { Button } from "@/components/ui/button";
import { useArtistRepository } from "@/pages/artists/hooks/use-artist-repository";
import { ArtistNestedType } from "@/pages/artists/schemas/artist.nested.form";

function formatMonthlyListeners(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return n.toLocaleString();
}

export function LineupFormField({ id, inputProps }: AutoFormFieldProps) {
  const { search } = useArtistRepository();
  const form = useFormContext();
  const value: ArtistNestedType[] = useWatch({ name: id }) ?? [];

  const setValue = (newValue: ArtistNestedType[]) => {
    inputProps.onChange({
      target: { name: id, value: newValue },
    } as unknown as React.ChangeEvent<HTMLInputElement>);
  };

  const genres = form.watch("genres") ?? [];

  return (
    <div className="flex flex-col space-y-2 text-foreground w-full">
      <SortableList
        handle
        items={value.map((l) => ({
          ...l,
          id: l.id ?? l.name,
        }))}
        render={(item, index) => (
          <div className="flex flex-col space-y-2 w-full" key={index}>
            <div className="flex flex-row space-x-2 w-full items-end">
              {item.photo && (
                <PhotoWithBlurhash
                  className="h-10 rounded-full"
                  url={item.photo?.url ?? ""}
                  blurhash={item.photo?.blurhash ?? ""}
                />
              )}
              <RemoteSelect<ArtistNestedType>
                key={JSON.stringify(value)}
                parentClassName="w-full"
                className={item.id === item.name ? "border-amber-400" : ""}
                triggerType="change"
                value={item}
                disabled={inputProps?.disabled}
                defaultOpen={index === value.length - 1 && item.name === ""}
                onValueChanged={(selected) => {
                  const newValues = [...value];
                  newValues[index!] = selected as ArtistNestedType;
                  setValue(newValues);
                }}
                accessorFn={(option) => ({
                  id: option?.id || option?.name,
                  name: option ? `${option?.name}` : "",
                  iconUrl: option?.media?.photos[0]?.url,
                  number:
                    option?.stats?.spotify?.monthlyListeners?.toString() ??
                    "NaN",
                })}
                onCreate={(query) => {
                  return { name: query } as ArtistNestedType;
                }}
                onSearch={async (query, onStatusChanged) => {
                  onStatusChanged("loading");
                  const result = await search(query);
                  onStatusChanged("success");

                  return result;
                }}
              />

              <Button
                variant="secondary"
                size="icon"
                type="button"
                className="hover:bg-zinc-300 hover:text-black focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 dark:bg-white dark:text-black dark:hover:bg-zinc-300 dark:hover:text-black dark:hover:ring-0 dark:hover:ring-offset-0 dark:focus-visible:ring-0 dark:focus-visible:ring-offset-0"
                onClick={() =>
                  setValue(
                    value.filter((v) =>
                      _.isNil(v.id) ? v.name !== item.name : v.id !== item.id,
                    ),
                  )
                }
              >
                <Trash className="size-4" />
              </Button>
            </div>
            {item.stats?.spotify?.monthlyListeners != null && (
              <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#1DB954]/10 border border-[#1DB954]/25 w-fit">
                <svg
                  viewBox="0 0 24 24"
                  className="size-3 fill-[#1DB954] shrink-0"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
                </svg>
                <span className="text-xs font-medium text-[#1DB954]">
                  {formatMonthlyListeners(item.stats.spotify.monthlyListeners)}
                </span>
                <span className="text-xs text-muted-foreground">
                  monthly listeners
                </span>
              </div>
            )}
            <div className="flex flex-row flex-wrap gap-2">
              {item.genres?.length &&
                item.genres.length > 0 &&
                item.genres.map((g: { id: string; name: string }) => (
                  <BadgeSelector
                    key={g.id}
                    item={g}
                    values={genres}
                    onChange={(newGenres) => {
                      console.debug(newGenres);
                      form.setValue("genres", newGenres, { shouldDirty: true });
                    }}
                  />
                ))}
            </div>
          </div>
        )}
        onChanged={(items) => {
          setValue(
            items.map((i: any) => {
              if (i.id === i.name) {
                return { id: null, name: i.name } as ArtistNestedType;
              }
              return i;
            }),
          );
        }}
        orientation="vertical"
      />
      <Button
        type="button"
        variant="secondary"
        onClick={() => setValue([...value, { name: "" } as ArtistNestedType])}
        className="flex items-center text-foreground"
      >
        <Plus className="mr-2" size={16} />
        Add
      </Button>
    </div>
  );
}
